'use client';
import React from 'react';
import { useModel } from '@/context/ModelContext';
import KPIBentoGrid from './KPIBentoGrid';
import RevenueCostChart from './RevenueCostChart';
import EbitdaPatChart from './EbitdaPatChart';
import FundingPieChart from './FundingPieChart';
import CapexBreakdownChart from './CapexBreakdownChart';
import ScenarioSwitcher from './ScenarioSwitcher';
import RecommendationCard from './RecommendationCard';
import CumulativeFcfChart from './CumulativeFcfChart';
import SensitivityHeatmap from '../scenario-comparison/components/SensitivityHeatmap';
import { fmtCrShort } from '@/lib/financial-model';
import { TrendingUp, AlertTriangle, CheckCircle2, Clock, FileText, Sun } from 'lucide-react';

export default function DashboardContent() {
  const { model, assumptions, updateAssumption } = useModel();

  return (
    <div className="space-y-6">
      {/* Page header — executive document style */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="gold-bar h-7" />
              <div>
                <h1
                  style={{
                    fontSize: '1.375rem',
                    fontWeight: 800,
                    color: 'var(--text-primary)',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Executive Dashboard
                </h1>
                <p
                  style={{
                    fontSize: '0.65rem',
                    color: 'var(--text-tertiary)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    marginTop: 2,
                  }}
                >
                  Shiva Green Energy · 5 TPH Biomass Pellet Plant · Lean Base Case · No Solar · Aug
                  2026
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="badge-success">
              <CheckCircle2 size={10} />
              GO WITH CONDITIONS
            </span>
            <span className="badge-info">
              <TrendingUp size={10} />
              10-Year Horizon
            </span>
            <span className="badge-warning">
              <FileText size={10} />
              CONFIDENTIAL
            </span>
            <button 
              onClick={() => updateAssumption('isSolarPhase2Enabled', !assumptions.isSolarPhase2Enabled)}
              className="px-3 py-1.5 rounded-full flex items-center gap-2 transition-all"
              style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                background: assumptions.isSolarPhase2Enabled ? 'var(--gold)' : 'var(--surface-3)',
                color: assumptions.isSolarPhase2Enabled ? 'var(--primary-foreground)' : 'var(--text-secondary)',
                border: assumptions.isSolarPhase2Enabled ? '1px solid var(--gold)' : '1px solid var(--divider)',
              }}
            >
              <Sun size={12} />
              SOLAR PHASE-2 {assumptions.isSolarPhase2Enabled ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>
        <div className="exec-rule mt-4" />
      </div>

      {/* Scenario Switcher */}
      <ScenarioSwitcher />

      {/* KPI Bento Grid */}
      <KPIBentoGrid />

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 exec-panel">
          <div className="exec-panel-header flex items-center justify-between">
            <div>
              <h3
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Revenue vs Total Cost
              </h3>
              <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                10-Year trend · ₹ Crores
              </p>
            </div>
            <span className="badge-success">Year 1–10</span>
          </div>
          <div className="p-5">
            <RevenueCostChart />
          </div>
        </div>
        <div className="exec-panel">
          <div className="exec-panel-header">
            <h3
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Funding Structure
            </h3>
            <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
              Equity vs Debt · {fmtCrShort(model?.totalProjectCost)} total
            </p>
          </div>
          <div className="p-5">
            <FundingPieChart />
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 exec-panel">
          <div className="exec-panel-header flex items-center justify-between">
            <div>
              <h3
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                EBITDA & PAT Trend
              </h3>
              <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                Operating profitability · ₹ Crores
              </p>
            </div>
            <span className="badge-info">
              <Clock size={10} />
              Yr2 EBITDA {model?.years?.[2] ? fmtCrShort(model?.years?.[2]?.ebitda) : '—'}
            </span>
          </div>
          <div className="p-5">
            <EbitdaPatChart />
          </div>
        </div>
        <div className="exec-panel">
          <div className="exec-panel-header">
            <h3
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              CAPEX Breakdown
            </h3>
            <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
              Uses of funds
            </p>
          </div>
          <div className="p-5">
            <CapexBreakdownChart />
          </div>
        </div>
      </div>

      {/* Cumulative FCF */}
      <div className="exec-panel">
        <div className="exec-panel-header flex items-center justify-between">
          <div>
            <h3
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Cumulative Project Cash Flow — Payback Visualization
            </h3>
            <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
              Payback at Year ~
              {model?.paybackYearFractional > 0 ? model?.paybackYearFractional?.toFixed(1) : 'N/A'}{' '}
              · ₹ Crores
            </p>
          </div>
          <span className="badge-warning">
            <AlertTriangle size={10} />
            Payback Yr {model?.paybackYear > 0 ? model?.paybackYear : 'N/A'}
          </span>
        </div>
        <div className="p-5">
          <CumulativeFcfChart />
        </div>
      </div>

      {/* Recommendation Card */}
      <RecommendationCard />

      {/* Sensitivity Heatmap */}
      <SensitivityHeatmap />

      {/* Model Notes */}
      <div
        className="rounded p-5"
        style={{ background: 'rgba(74,158,255,0.04)', border: '1px solid rgba(74,158,255,0.15)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-px h-4" style={{ background: 'var(--accent)' }} />
          <h3
            style={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--accent)',
            }}
          >
            Model Notes & Assumptions
          </h3>
        </div>
        <ul className="space-y-1.5">
          {[
            'Solar has been dropped from the Base Case. Power cost is full grid (₹1,100/tonne).',
            'Carbon credits treated as Upside only — not included in Base P&L.',
            'All amber cells on Assumptions sheet are editable inputs — changes recalculate instantly.',
            'Model uses conservative-to-realistic assumptions for bankability.',
            'Entity currently partnership firm — conversion to Pvt Ltd recommended before large debt.',
            'CAPEX scaled non-linearly: machinery ~2.5×, civil ~1.9×, WC ~3.5× vs 2 TPH baseline.',
            '10-year explicit forecast with simplified debt amortisation. No terminal value in IRR/NPV.',
          ]?.map((note, idx) => (
            <li key={`note-${idx}`} className="flex items-start gap-2">
              <span
                style={{
                  color: 'var(--accent)',
                  fontWeight: 700,
                  marginTop: 1,
                  flexShrink: 0,
                  fontSize: '0.65rem',
                }}
              >
                {idx + 1}.
              </span>
              <span
                style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}
              >
                {note}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
